import { NpoPlayerServices } from './services/services';
import { Player, PlayerEvent } from 'bitmovin-player';
import AdsModuleBM from 'bitmovin-player/modules/bitmovinplayer-advertising-bitmovin';
import { logEvent, initPlayerTracker, startPlayerTracker } from './js/tracking/playertracker';
import { hidePlayNextScreen, showPlayNextScreenIfNeeded } from './ui/handlers/playnextscreen';
import * as playerAction from './js/playeractions/playeractions';
import { getModuleExport } from './js/utilities/utilities.module';
import { getStreamObject } from './js/api/getstreamobject';
import { isJWTToken } from './js/utilities/utilities.jwt';
import { isMediaUrl, isUrl } from './js/utilities/utilities.url';
import { logVersion } from './js/utilities/utilities.version';
import { getStreamDurationInSeconds } from './js/utilities/utilities.stream';
import { LogEmitter } from './types/classes';
import pkg from '../package.json';
import { NpoPlayerUIVariants, LocalStorageValues } from './types/interfaces';
import { nativeMobileUiFactory } from './ui/nativemobileuifactory';
import { setupMediaSessionActionHandlers } from './js/playeractions/handlers/mediasessionactions';
import { removeReplayClass } from './js/playeractions/handlers/removereplayclass';
import { updateLiveMarkers } from './js/markers/updateLiveMarkers';
import { NpoPlayerAPI } from './services/npoPlayerAPI/npoPlayerAPI';
export { NpoPlayerUIVariants };
export default class NpoPlayer {
    constructor(container, playerConfig, npoTag, npoTagInstance, variant, npoTagPageTracker) {
        this.sourceConfig = {};
        this.streamObject = {
            stream: { drmType: '', streamProfile: '', streamURL: '', avType: '', sourceProfile: '' },
            metadata: { title: '', description: '' },
            assets: { scrubbingThumbnail: '', subtitles: [] },
            user: { type: 'anonymous' }
        };
        this.player = undefined;
        this.uiManager = undefined;
        this.npoTagInitialisation = undefined;
        this.npoTagInstance = undefined;
        this.npoTagPageTracker = undefined;
        this.streamTracker = undefined;
        this.logEmitter = new LogEmitter();
        this.uiComponents = {};
        this.streamOptions = {};
        this.jwt = '';
        this.apiPayload = { baseURL: '', jwt: '', data: {} };
        this.adBreakActive = false;
        this.version = pkg.version;
        this.drmProfile = { profileName: '', drm: '' };
        this.isShowingPlayNextScreen = false;
        this.canceledPlayNextScreen = false;
        this.playerContext = undefined;
        this.npoplayerServices = new NpoPlayerServices();
        this.eventListeners = undefined;
        this.userPreferences = {};
        if (typeof container === 'object' && !(container instanceof HTMLElement)) {
            const params = container;
            this.container = params.container;
            this.playerConfig = params.playerConfig;
            this.npoTagInitialisation = params.npoTag || undefined;
            this.npoTagInstance = params.npoTagInstance;
            this.variant = params.variant || NpoPlayerUIVariants.DEFAULT;
            this.npoTagPageTracker = params.npoTagPageTracker || undefined;
        }
        else {
            this.container = container;
            this.playerConfig = playerConfig || undefined;
            this.npoTagInitialisation = npoTag || undefined;
            this.npoTagInstance = npoTagInstance;
            this.variant = variant || NpoPlayerUIVariants.DEFAULT;
            this.npoTagPageTracker = npoTagPageTracker || undefined;
            console.warn('As of NPO Player version 1.25, parameter-based initialization has been deprecated in favor of object-based initialization. This feature will be removed in version 2.0. Please update your implementation to ensure compatibility. For more details, refer to the documentation at: https://docs.npoplayer.nl/implementation/web/player.');
        }
        if (!this.playerConfig) {
            console.error('Player configuration is missing. Ensure that playerConfig is properly initialized before using the player.');
            return;
        }
        if (this.npoTagInstance) {
            initPlayerTracker(this, this.npoTagInitialisation, this.npoTagInstance, this.npoTagPageTracker);
        }
        this.initPlayer(this.container, this.playerConfig);
    }
    initPlayer(_container, playerConfig) {
        const processedPlayerConfig = playerAction.processPlayerConfig(this, playerConfig);
        this.player = new Player(_container, processedPlayerConfig);
        Player.addModule(getModuleExport(AdsModuleBM));
        const npoPlayerAPI = new NpoPlayerAPI(this.player);
        this.playerContext = { player: npoPlayerAPI, npoplayer: this };
        this.userPreferences = this.npoplayerServices.getStoredUserPrefs();
        this.npoplayerServices.handleUserPrefs(this.playerContext);
        this.npoplayerServices.setAccessibilityAttributes(this.playerContext);
        window.addEventListener('beforeunload', () => {
            if (this.player) {
                this.npoplayerServices.setStoredUserPrefs(LocalStorageValues.SUBTITLES_ENABLED, npoPlayerAPI.areSubtitlesEnabled().toString());
                this.npoplayerServices.setStoredUserPrefs(LocalStorageValues.VOLUME, npoPlayerAPI.getVolume());
                this.npoplayerServices.setStoredUserPrefs(LocalStorageValues.IS_MUTED, npoPlayerAPI.isMuted().toString());
            }
        });
        this.container.addEventListener('keydown', (e) => {
            if (!this.playerContext)
                return;
            this.npoplayerServices.keyboardHandler(this.playerContext, e);
        }, true);
    }
    async loadStream(source, options = {}) {
        if (!this.playerContext)
            return;
        this.streamOptions = options;
        if (this.player == undefined) {
            console.error('Er is nog geen player geladen.');
            return;
        }
        if (this.adBreakActive) {
            const currentAd = this.player.ads.getActiveAdBreak().id;
            this.player.ads.discardAdBreak(currentAd);
        }
        let _streamObject;
        const sourceIsUrl = isUrl(source);
        const sourceIsMedia = await isMediaUrl(source);
        const sourceIsJWTToken = isJWTToken(source);
        const getDurationAndStartPlayerTracker = function () {
            if (this.player === null)
                return;
            const prid = (isJWTToken(source) ? this.streamObject?.metadata.prid : source) || 'unknown';
            const duration = this.player ? this.player.getDuration() : undefined;
            startPlayerTracker(this, getStreamDurationInSeconds({ duration: duration }), this.version, prid);
        }.bind(this);
        if (sourceIsUrl && sourceIsMedia) {
            this.sourceConfig = {
                ...options.sourceConfig,
                progressive: source,
                analytics: {
                    customData2: 'other',
                    customData3: 'unknown',
                    customData4: this.npoTagInstance?.getParty(),
                    customData5: this.version
                }
            };
            await this.playerContext.player?.load(this.sourceConfig);
            getDurationAndStartPlayerTracker();
        }
        else if (sourceIsJWTToken) {
            this.jwt = source;
            const profile = await this.npoplayerServices.decideProfile(this.playerContext, options?.preferredDRM ?? '');
            const defaultEndpoint = 'https://prod.npoplayer.nl/';
            const endpoint = options?.endpoint ?? defaultEndpoint;
            const payload = {
                baseURL: endpoint,
                jwt: source,
                data: {
                    profileName: profile.profileName,
                    drmType: profile.drm,
                    referrerUrl: window.location.href,
                    ster: {
                        identifier: options?.ster?.identifier ?? 'npo-app-desktop',
                        deviceType: options?.ster?.deviceType ?? 4,
                        player: 'web'
                    }
                }
            };
            try {
                _streamObject = await getStreamObject(this, payload);
                this.streamObject = _streamObject;
                if (_streamObject) {
                    await this.playerContext.player.createUIManager(this.playerContext, this.variant);
                }
            }
            catch (error) {
                this.doError('Het is niet gelukt de stream op te halen. \n' + error);
                this.player.pause();
                return;
            }
            if (this.streamObject?.stream == undefined)
                return;
            const drmType = this.streamObject.stream.drmType ?? undefined;
            this.sourceConfig = await playerAction.processSourceConfig(this.npoplayerServices, source, options.sourceConfig ?? {}, this.streamObject, drmType && drmType.length > 0 ? profile.drm : undefined, this.streamOptions, this.version, this.npoTag?.npoTagInstance);
            await this.npoplayerServices.verifyDRM(this.playerContext, payload);
            setupMediaSessionActionHandlers(this.player, this.sourceConfig, _streamObject);
            logEvent(this, 'load');
            let streamDuration = _streamObject.metadata.duration;
            const streamPrid = this.streamObject.metadata.prid || _streamObject.metadata.prid;
            if (this.isShowingPlayNextScreen) {
                this.hidePlayNextScreen();
            }
            const initAndStartTracker = () => {
                if (this.player === null)
                    return;
                this.player?.off(PlayerEvent.AdBreakFinished, initAndStartTracker);
                this.player?.off(PlayerEvent.AdError, initAndStartTracker);
                streamDuration =
                    streamDuration == undefined
                        ? getDurationAndStartPlayerTracker()
                        : startPlayerTracker(this, getStreamDurationInSeconds({ duration: streamDuration, durationIsInMs: true }), this.version, streamPrid);
            };
            if (this.variant === NpoPlayerUIVariants.DEFAULT &&
                this.streamObject.metadata.hasPreroll === 'true' &&
                this.streamObject.assets.preroll) {
                this.player.on(PlayerEvent.AdBreakFinished, initAndStartTracker);
                this.player.on(PlayerEvent.AdError, initAndStartTracker);
                await this.npoplayerServices.schedulePreRolls(this.playerContext);
            }
            else {
                initAndStartTracker();
            }
        }
        else {
            this.doError(`Het is niet gelukt de stream op te halen: \n Input is geen valide token of media object.`, 500);
        }
        this.npoplayerServices.setupNicamKijkwijzerIcons(this.playerContext);
        this.player.on(PlayerEvent.Play, () => {
            this.npoplayerServices.showNicamAfterUiDelay(this.playerContext, this.uiManager);
        });
        this.npoplayerServices.handleVerticalVideoControls(this.playerContext, this.variant);
        this.player.on(PlayerEvent.Seek, () => {
            removeReplayClass(this.player);
        });
        if (options?.playNext?.showPlayNext === true) {
            this.player.on(PlayerEvent.TimeChanged, () => {
                this.showPlayNextScreen();
            });
            this.player.on(PlayerEvent.Paused, () => {
                this.hidePlayNextScreen();
            });
        }
        const setLiveOffsetListener = function () {
            if (this.playerContext === undefined)
                return;
            this.player?.off(PlayerEvent.Ready, setLiveOffsetListener);
            playerAction.handleLiveOffsetLogic(this.playerContext, options);
        }.bind(this);
        this.player.on(PlayerEvent.Ready, setLiveOffsetListener);
    }
    doError(input, status) {
        if (this.player == undefined)
            return;
        if (status) {
            this.logEmitter.emit('logError', status);
        }
        playerAction.handlePlayerError(this.player, this.uiComponents, input);
    }
    async play() {
        await this.playerContext?.player.play();
    }
    pause() {
        this.playerContext?.player.pause();
    }
    setVolume(volume) {
        if (this.playerContext === undefined)
            return;
        this.playerContext.player.setVolume(volume);
    }
    increaseVolume() {
        if (this.playerContext === undefined)
            return;
        this.playerContext.player.increaseVolume();
    }
    decreaseVolume() {
        if (this.playerContext === undefined)
            return;
        this.playerContext.player.decreaseVolume();
    }
    goForward(seconds) {
        if (this.player == undefined)
            return;
        if (this.player.isLive() ?? false) {
            this.player.timeShift(Math.min(0, this.player.getTimeShift() + seconds));
        }
        else {
            this.player.seek(this.player.getCurrentTime() + seconds);
        }
    }
    goBackwards(seconds) {
        if (this.player == undefined)
            return;
        if (this.player.isLive() ?? false) {
            this.player.timeShift(this.player.getTimeShift() - seconds);
        }
        else {
            this.player.seek(this.player.getCurrentTime() - seconds);
        }
    }
    watchFromStart() {
        if (this.playerContext == undefined)
            return;
        playerAction.shiftToProgramStart(this.playerContext, this.streamOptions.liveProgramTime);
    }
    showPlayNextScreen() {
        if (this.player == undefined || !this.streamOptions.playNext?.showPlayNext || this.adBreakActive)
            return;
        this.player.off(PlayerEvent.TimeChanged, () => {
            this.showPlayNextScreen();
        });
        const videoDuration = this.player.getDuration();
        const overlayDuration = this.streamOptions.playNext?.duration || 10;
        const offset = this.streamOptions.playNext?.offset || overlayDuration;
        const showPlayNextAt = videoDuration - offset;
        if (!this.isShowingPlayNextScreen &&
            this.player.getCurrentTime() > showPlayNextAt &&
            !this.canceledPlayNextScreen) {
            this.isShowingPlayNextScreen = true;
            showPlayNextScreenIfNeeded(overlayDuration, this.container, this.streamOptions.playNext?.proceedCallback);
        }
        else if (this.isShowingPlayNextScreen && this.player.getCurrentTime() < showPlayNextAt) {
            this.isShowingPlayNextScreen = false;
            hidePlayNextScreen(this.container);
        }
    }
    updateMarkers(timeLineMarkers) {
        if (!(this.player && this.uiManager && this.streamObject.stream.isLiveStream))
            return;
        updateLiveMarkers(timeLineMarkers, this.player, this.uiManager, this.streamOptions.autoFillTimeLineMarkerDuration ?? true);
    }
    cancelPlayNextScreen() {
        this.canceledPlayNextScreen = true;
        this.hidePlayNextScreen();
        this.streamOptions.playNext?.cancelCallback?.();
    }
    hidePlayNextScreen() {
        this.player?.off(PlayerEvent.Paused, () => {
            this.hidePlayNextScreen();
        });
        this.isShowingPlayNextScreen = false;
        hidePlayNextScreen(this.container);
    }
    doPlayNext() {
        this.hidePlayNextScreen();
        this.streamOptions.playNext?.proceedCallback?.();
    }
    destroy(asyncMode = false) {
        const destroyLogic = async () => {
            try {
                if (this.npoTag != undefined) {
                    clearInterval(this.npoTag?.heartbeatInterval);
                }
                logEvent(this, 'stop');
                this.uiManager?.release();
                this.uiManager = undefined;
                await this.player?.destroy();
                return true;
            }
            catch (error) {
                console.error('Error destroying player:', error);
                return false;
            }
        };
        return asyncMode
            ? destroyLogic()
                .then(() => true)
                .catch(() => false)
            : destroyLogic();
    }
    unload(asyncMode = false) {
        const unloadLogic = async () => {
            try {
                if (this.npoTag != undefined) {
                    clearInterval(this.npoTag.heartbeatInterval);
                }
                this.hidePlayNextScreen();
                await this.player?.unload();
                return true;
            }
            catch (error) {
                console.error('Error unloading player:', error);
                return false;
            }
        };
        if (asyncMode) {
            return unloadLogic();
        }
        else {
            try {
                return unloadLogic();
            }
            catch {
                return false;
            }
        }
    }
    printVersion() {
        logVersion(this.version);
    }
}
;
window.bitmovin.playerui = function () {
};
window.bitmovin.playerui.UIFactory = {
    buildDefaultSmallScreenUI: function (player, config = {}) {
        return nativeMobileUiFactory(player, config);
    }
};
